import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Badge,
  Box,
  Flex,
  Icon,
  IconButton,
  Image as ChakraImage,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { MdCloudUpload } from 'react-icons/md';
import { MAX_FILES } from '../config';
import { imageStore } from '../stores/imageStore';
import { uuidv4 } from '../utils/uuid';

const ImageUploader = ({ store = imageStore }) => {
  const [files, setFiles] = useState([]);

  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const bgBase = useColorModeValue('gray.50', 'gray.800');
  const textMuted = useColorModeValue('gray.500', 'gray.400');

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      id: uuidv4(),
      file,
      preview: URL.createObjectURL(file),
    }));
    setFiles((prev) => [...prev, ...newFiles].slice(0, MAX_FILES));
    newFiles.forEach((img) => store.addImage({ id: img.id, file: img.file }));
  }, [store]);

  const removeImage = (e, idToRemove) => {
    e.stopPropagation();
    e.preventDefault();
    store.removeImage(idToRemove);
    setFiles((curr) => curr.filter((f) => f.id !== idToRemove));
  };

  useEffect(() => {
    return () => files.forEach((f) => URL.revokeObjectURL(f.preview));
  }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/gif': [], 'image/webp': [] },
    maxFiles: MAX_FILES,
  });

  /* ── Empty state ── */
  if (files.length === 0) {
    return (
      <Box
        {...getRootProps()}
        p={8}
        border="2px dashed"
        borderColor={isDragActive ? 'blue.400' : borderColor}
        borderRadius="2xl"
        bg={isDragActive ? activeBg : bgBase}
        cursor="pointer"
        textAlign="center"
        transition="all 0.2s"
        _hover={{ borderColor: 'blue.400', bg: activeBg }}
      >
        <input {...getInputProps()} />
        <VStack spacing={3}>
          <Icon
            as={MdCloudUpload}
            boxSize={10}
            color={isDragActive ? 'blue.400' : 'gray.400'}
            transition="color 0.2s"
          />
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              {isDragActive ? 'Drop your images here' : `Drag & drop up to ${MAX_FILES} images`}
            </Text>
            <Text fontSize="xs" color={textMuted} mt={0.5}>
              or click to browse — JPG, PNG, GIF, WEBP
            </Text>
          </Box>
          <Badge colorScheme="blue" variant="subtle" fontSize="xs">
            100% Local — Never Uploaded
          </Badge>
        </VStack>
      </Box>
    );
  }

  /* ── Filled state: preview grid ── */
  return (
    <Box
      {...getRootProps()}
      border="2px dashed"
      borderColor={isDragActive ? 'blue.400' : borderColor}
      borderRadius="2xl"
      bg={isDragActive ? activeBg : bgBase}
      p={3}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ borderColor: 'blue.400' }}
    >
      <input {...getInputProps()} />
      <SimpleGrid columns={3} spacing={2}>
        {files.map((file, index) => (
          <Box key={file.id} position="relative" width="100%" pt="100%" overflow="hidden" borderRadius="md">
            <ChakraImage
              src={file.preview}
              alt={`preview ${index}`}
              position="absolute"
              top="0" left="0"
              width="100%" height="100%"
              objectFit="cover"
            />
            <IconButton
              aria-label="Remove image"
              icon={<CloseIcon />}
              size="xs"
              colorScheme="red"
              position="absolute"
              top="1" right="1"
              zIndex="1"
              onClick={(e) => removeImage(e, file.id)}
            />
          </Box>
        ))}
      </SimpleGrid>
      {files.length < MAX_FILES && (
        <Flex justify="center" mt={2}>
          <Text fontSize="xs" color={textMuted}>
            Drop more images ({files.length}/{MAX_FILES})
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default ImageUploader;
